import { useLanguage } from './components/LanguageProvider.jsx';
import AdminLocalizedPage from './components/AdminLocalizedPage.jsx';
import { translateAdminText } from './lib/admin-i18n.js';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { api, session } from './lib/api.js';
import { applyUpdate, subscribeUpdate } from './lib/install.js';
import { clearCache, invalidateFromEvent } from './lib/cache.js';
import { connectRealtime } from './lib/realtime.js';
import AppShell from './components/AppShell.jsx';
import Login from './pages/Login.jsx';

const Dashboard=lazy(()=>import('./pages/Dashboard.jsx'));
const POS=lazy(()=>import('./pages/POS.jsx'));
const Products=lazy(()=>import('./pages/Products.jsx'));
const Customers=lazy(()=>import('./pages/Customers.jsx'));
const DailySales=lazy(()=>import('./pages/DailySales.jsx'));
const Inventory=lazy(()=>import('./pages/Inventory.jsx'));
const Purchases=lazy(()=>import('./pages/Purchases.jsx'));
const Suppliers=lazy(()=>import('./pages/Suppliers.jsx'));
const Reports=lazy(()=>import('./pages/Reports.jsx'));
const LegacySales=lazy(()=>import('./pages/LegacySales.jsx'));
const Pricing=lazy(()=>import('./pages/Pricing.jsx'));
const Margins=lazy(()=>import('./pages/Margins.jsx'));
const Employees=lazy(()=>import('./pages/Employees.jsx'));
const Account=lazy(()=>import('./pages/Account.jsx'));
const AuditLog=lazy(()=>import('./pages/AuditLog.jsx'));
const ProductDetails=lazy(()=>import('./pages/ProductDetails.jsx'));
const CustomerDetails=lazy(()=>import('./pages/CustomerDetails.jsx'));
const DateLabels=lazy(()=>import('./pages/DateLabels.jsx'));
const Expenses=lazy(()=>import('./pages/Expenses.jsx'));
const ProfitLoss=lazy(()=>import('./pages/ProfitLoss.jsx'));
const VatReturn=lazy(()=>import('./pages/VatReturn.jsx'));

const pages={dashboard:Dashboard,pos:POS,products:Products,customers:Customers,'daily-sales':DailySales,inventory:Inventory,purchases:Purchases,suppliers:Suppliers,reports:Reports,'legacy-sales':LegacySales,pricing:Pricing,margins:Margins,employees:Employees,account:Account,audit:AuditLog,product:ProductDetails,customer:CustomerDetails,'date-labels':DateLabels,expenses:Expenses,pnl:ProfitLoss,'vat-return':VatReturn};

// التسلسل المنطقي للرجوع — لا يعتمد على تاريخ المتصفح
const PARENT={product:'products',customer:'customers'};
// الشاشات الرئيسية (أقسام القائمة): الرجوع منها دائماً إلى النظرة العامة
const MAIN=new Set(['dashboard','pos','daily-sales','customers','employees','products',
  'inventory','purchases','suppliers','expenses','pnl','reports','legacy-sales','pricing','margins','audit','account','date-labels']);

export default function App(){
  const { t, language } = useLanguage();
  const [user,setUser]=useState(null); const [loading,setLoading]=useState(Boolean(session.get()));
  // مكدّس تنقّل منطقي — لا يعتمد على تاريخ المتصفح (PWA مثبَّت بلا شريط عنوان)
  const [stack,setStack]=useState([{view:'dashboard',params:{}}]);
  const current=stack[stack.length-1];
  const view=current.view;
  const [guard,setGuard]=useState(null);       // دالة تُرجع رسالة تحذير أو null
  const [confirmLeave,setConfirmLeave]=useState(null);
  const guardRef=useRef(null); guardRef.current=guard;
  const rootRef=useRef('dashboard');

  const doNavigate=(next,params={})=>setStack(s=>{
    const top=s[s.length-1];
    if(next===top.view && JSON.stringify(params)===JSON.stringify(top.params)) return s;
    const root=rootRef.current;
    // شاشة رئيسية: المكدّس = [الجذر، الشاشة] فيرجع منها للنظرة العامة مباشرة
    if(MAIN.has(next))
      return next===root ? [{view:root,params:{}}] : [{view:root,params:{}},{view:next,params}];
    // شاشة تفصيلية: تُكدَّس فوق شاشتها الأم ليعمل الرجوع منطقياً
    const parent=PARENT[next];
    if(parent && top.view!==parent)
      return [{view:root,params:{}},{view:parent,params:{}},{view:next,params}];
    return [...s,{view:next,params}];
  });
  // الرجوع: شاشة فرعية ← أمّها · شاشة رئيسية ← النظرة العامة (الجذر)
  const doBack=()=>setStack(s=>{
    if(s.length>1) return s.slice(0,-1);
    return [{view:rootRef.current,params:{}}];
  });

  const attempt=(action)=>{
    const warn=guardRef.current?.();
    if(warn) return setConfirmLeave({warn,action});
    action();
  };
  const navigate=(next,params)=>attempt(()=>doNavigate(next,params));
  const back=()=>attempt(doBack);
  const canGoBack=stack.length>1;

  // زر الرجوع في جهاز أندرويد داخل PWA
  useEffect(()=>{
    window.history.pushState({bh:true},'');
    const onPop=()=>{ window.history.pushState({bh:true},''); if(stack.length>1) back(); };
    window.addEventListener('popstate',onPop);
    return ()=>window.removeEventListener('popstate',onPop);
  },[stack.length]);
  const [syncEvent,setSyncEvent]=useState(null); const [syncStatus,setSyncStatus]=useState('connecting');
  const [updateReady,setUpdateReady]=useState(false);
  useEffect(()=>subscribeUpdate(setUpdateReady),[]);
  const UpdateBanner=()=>updateReady?<div className="update-banner">
    <span>{t("app.update")}</span>
    <button onClick={applyUpdate}>{t("app.updateNow")}</button></div>:null;
  const resetTo=(root)=>setStack([{view:root,params:{}}]);
  useEffect(()=>{if(!session.get())return;api('/me').then(({user:me})=>{setUser(me);resetTo(me.role==='admin'?'dashboard':'pos');}).catch(()=>session.clear()).finally(()=>setLoading(false));},[]);
  // أبطِل الكاش أولاً ثم أبلغ الشاشات، حتى لا تقرأ الشاشة نسخة قديمة
  useEffect(()=>{if(!user)return;return connectRealtime({
    onEvent:(e)=>{invalidateFromEvent(e);setSyncEvent(e);},onStatus:setSyncStatus});},[user?.id]);
  const login=(data)=>{session.set(data.token);setUser(data.user);resetTo(data.user.role==='admin'?'dashboard':'pos');};
  const logout=async()=>{
    try{await api('/auth/logout',{method:'POST'});}
    catch{/* الجلسة تُنهى محلياً حتى لو تعذّر الاتصال */}
    finally{
      clearCache();session.clear();setUser(null);setGuard(null);resetTo('dashboard');
      // امنع الرجوع بزر المتصفح إلى شاشة داخل النظام
      try{window.history.replaceState(null,'',window.location.pathname);}catch{}
    }
  };
  if(loading)return <div className="app-loading"><span className="spinner"/><p>{t("app.preparing")}</p></div>;
  if(!user)return <><Login onLogin={login}/><UpdateBanner/></>;
// الكاشير محصور في نقطة البيع وملصقات التواريخ وإعدادات حسابه — أي شاشة أخرى تُعاد إلى نقطة البيع
const CASHIER_VIEWS=['pos','account','date-labels'];
  const allowed=user.role==='admin'||CASHIER_VIEWS.includes(view);
  const Page=allowed?(pages[view]||pages[user.role==='admin'?'dashboard':'pos']):pages.pos;
  const ROOT=user.role==='admin'?'dashboard':'pos';
  rootRef.current=ROOT;
  // زر الرجوع يظهر في كل شاشة عدا الجذر — حتى الشاشات الرئيسية ترجع للنظرة العامة
  const showBack=view!==ROOT;
  // الخروج من الهيكل يمرّ بالحارس نفسه — فاتورة غير مكتملة تُنبّه قبل ضياعها
  return <AppShell user={user} view={view} onNavigate={navigate} onLogout={()=>attempt(logout)} syncStatus={syncStatus}>
    <Suspense fallback={<div className="skeleton-page"/>}>
      {['dashboard','pos','account','date-labels'].includes(view)
        ? <Page user={user} navigate={navigate} back={back} canGoBack={showBack}
            params={current.params} setGuard={setGuard} syncEvent={syncEvent} onLogout={logout}
            onUserUpdate={(next)=>setUser(c=>({...c,...next}))}/>
        : <AdminLocalizedPage Page={Page} language={language} pageProps={{user,navigate,back,canGoBack:showBack,
            params:current.params,setGuard,syncEvent,onLogout:logout,
            onUserUpdate:(next)=>setUser(c=>({...c,...next}))}}/>}
    </Suspense>
    {confirmLeave?<div className="leave-confirm-backdrop" role="dialog">
      <div className="leave-confirm">
        <strong>{t("app.leaveTitle")}</strong>
        <p>{translateAdminText(confirmLeave.warn,language)}</p>
        <div className="leave-confirm__actions">
          <button className="button button--primary" onClick={()=>setConfirmLeave(null)}>{t("app.stay")}</button>
          <button className="button button--outline leave-confirm__leave" onClick={()=>{const a=confirmLeave.action;setConfirmLeave(null);setGuard(null);a();}}>{t("app.leave")}</button>
        </div>
      </div></div>:null}
    <UpdateBanner/>
  </AppShell>;
}
